import { Vector3 } from 'three';
import { IBuildingProviderContext } from '../../@types/ContextProviderRelated';
import { EWall } from '../../@types/Enums';

import BuildingLevelModel from '../../models/standard/BuildingLevelModel';
import BuildingModel, { IMultiRoomBuilding } from '../../models/standard/BuildingModel';
import CompassModel from '../../models/standard/CompassModel';

import RoomModel from '../../models/standard/RoomModel';
import SpaceModel from '../../models/standard/SpaceModel';

import PartitionWallModel from '../../models/standard/PartitionWallModel';
import StandardWallModel from '../../models/standard/StandardWallModel';

import FloorModel from '../../models/standard/FloorModel';
import TilesModel from '../../models/standard/TileModel';

import ElementModel from '../../models/standard/ElementModel';
import Model2DModel from '../../models/standard/Model2DModel';
import ProductModel from '../../models/standard/ProductModel';

import DoorModel from '../../models/standard/DoorModel';
import PassageModel from '../../models/standard/PassageModel';
import RadiatorModel from '../../models/standard/RadiatorModel';
import WindowModel from '../../models/standard/WindowModel';

import BoundaryEdge from '../../models/abstract/BoundaryEdge';
import PhysicalBoundaryModel, { IPhysicalBoundary } from '../../models/standard/PhysicalBoundary';
import VirtualBoundaryModel from '../../models/standard/VirtualBoundaryModel';

import { BOUNDARY_TYPE } from '../../models/abstract/Boundary';
import { IWall } from '../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../models/standard/AttributeModel';
import { IObject, ObjectModel } from '../../models/standard/ObjectModel';

// ------------------------------------------------------
// TYPE GUARD
// ------------------------------------------------------
function isPhysicalBoundary(b: any): b is IPhysicalBoundary {
  return b?.type === BOUNDARY_TYPE.PHYSICAL;
}

// ------------------------------------------------------
// ATTRIBUTE + OBJECT HELPERS
// ------------------------------------------------------

export const createAttributes = (attributes?: IAttribute[] | { attributes: IAttribute[] }): AttributeModel[] => {
  const arr = Array.isArray(attributes) ? attributes : attributes?.attributes;

  return (
    arr?.map(
      (a) =>
        new AttributeModel(
          a.id,
          a.key,
          a.value,
          a.predecessorId,
          a.type,
          a.unit,
          a.hidden,
          a.forced,
          a.ownerId,
          a.userId,
          a.publicFlag,
          a.localization,
          a.defaults,
          a.validation,
          a.unitFamily,
        ),
    ) ?? []
  );
};

export const createObject = (object?: IObject): ObjectModel | undefined => {
  if (!object) return undefined;
  return new ObjectModel(object.id, object.name, createAttributes(object.attributes), object.uuid, object.localization, object.ifcObject);
};

export const createObjects = (objects?: IObject[]): ObjectModel[] => objects ? objects.map((o) => new ObjectModel(o.id, o.name, createAttributes(o.attributes), o.uuid, o.localization, o.ifcObject)) : [];

// ------------------------------------------------------
// WALL CLONE HELPER
// ------------------------------------------------------
const cloneWallFromSource = (src: IWall): StandardWallModel | PartitionWallModel => {
  const start = new Vector3(src.startPoint.x, src.startPoint.y, src.startPoint.z);
  const end = new Vector3(src.endPoint.x, src.endPoint.y, src.endPoint.z);

  // 1. Prepare children (Shared logic for both wall types)
  const radiatorsA = src.sides.A.radiators.map((r: any) => {
    const m = new RadiatorModel(new Vector3(r.position.x, r.position.y, r.position.z), r.length, r.depth, r.angle, createAttributes(r.attributes), undefined, r.nicheDepth);
    m.setUUID(r.uuid);
    return m;
  });

  const radiatorsB = src.sides.B.radiators.map((r: any) => {
    const m = new RadiatorModel(new Vector3(r.position.x, r.position.y, r.position.z), r.length, r.depth, r.angle, createAttributes(r.attributes), undefined, r.nicheDepth);
    m.setUUID(r.uuid);
    return m;
  });

  const windows = src.windows.map((win: any) => {
    const m = new WindowModel(new Vector3(win.position.x, win.position.y, win.position.z), win.length, win.depth, win.angle, createAttributes(win.attributes));
    m.setUUID(win.uuid);
    return m;
  });

  const doors = src.doors.map((d: any) => {
    const m = new DoorModel(new Vector3(d.position.x, d.position.y, d.position.z), d.length, d.depth, d.angle, d.openingDirection, d.orientation, createAttributes(d.attributes));
    m.setUUID(d.uuid);
    return m;
  });

  const passages = (src.passages ?? []).map((p: any) => {
    const m = new PassageModel(new Vector3(p.position.x, p.position.y, p.position.z), p.length, p.depth, p.angle, createAttributes(p.attributes));
    m.setUUID(p.uuid);
    return m;
  });

  // 2. Instantiate specific wall type
  if (src.type === EWall.STANDARD) {
    const sw = src as StandardWallModel;

    const wallModel = new StandardWallModel(
      start,
      end,
      src.depth,
      windows,
      doors,
      [], // Legacy radiators empty
      createAttributes(src.attributes),
      undefined,
      passages,
    );

    // Assign Radiators to Sides
    wallModel.sides.A.radiators = radiatorsA;
    wallModel.sides.B.radiators = radiatorsB;
    wallModel.setUUID(src.uuid);

    // Outer geometry
    wallModel.setOuterStartPoint(new Vector3(sw.outerStartPoint.x, sw.outerStartPoint.y, sw.outerStartPoint.z));
    wallModel.setOuterEndPoint(new Vector3(sw.outerEndPoint.x, sw.outerEndPoint.y, sw.outerEndPoint.z));
    wallModel.setOuterStartPoint2(new Vector3(sw.outerStartPoint2.x, sw.outerStartPoint2.y, sw.outerStartPoint2.z));
    wallModel.setOuterEndPoint2(new Vector3(sw.outerEndPoint2.x, sw.outerEndPoint2.y, sw.outerEndPoint2.z));

    return wallModel;
  }

  // PARTITION WALL
  const part = new PartitionWallModel(
    start,
    end,
    src.depth,
    windows,
    doors,
    [], // Legacy radiators empty
    createAttributes(src.attributes),
    undefined,
    passages,
  );

  // FIX: Assign Radiators to Sides
  part.sides.A.radiators = radiatorsA;
  part.sides.B.radiators = radiatorsB;

  part.setUUID(src.uuid);
  return part;
};

// ------------------------------------------------------
// MAIN EXPORT FUNCTION
// ------------------------------------------------------
export const transformToMultiRoomBuilding = (building: IBuildingProviderContext): IMultiRoomBuilding => {
  const buildingModel = new BuildingModel([]);

  const compass = new CompassModel(building.compass.rotation ?? 0);

  const level0 = new BuildingLevelModel(0, [], compass);
  buildingModel.levels.push(level0);

  // ------------------------------------------------------
  // BUILD WALL MAP
  // ------------------------------------------------------
  const wallMap = new Map(building.walls.map((w) => [w.uuid, w]));

  const spaceWallUuids = new Set<string>();
  const spaceProductUuids = new Set<string>();
  const spaceElementUuids = new Set<string>();
  const spaceModel2DUuids = new Set<string>();

  // ------------------------------------------------------
  // BUILD ROOMS + SPACES
  // ------------------------------------------------------

  building.rooms.forEach((roomSrc) => {
    const room = new RoomModel(roomSrc.title, [], createAttributes(roomSrc.attributes));
    room.setUUID(roomSrc.uuid);

    roomSrc.spaces.forEach((spaceSrc) => {
      spaceSrc.walls.forEach((w) => {
        if (w.uuid) spaceWallUuids.add(w.uuid);
      });
      spaceSrc.products.forEach((p) => {
        if (p.uuid) spaceProductUuids.add(p.uuid);
      });
      spaceSrc.elements.forEach((e) => {
        if (e.uuid) spaceElementUuids.add(e.uuid);
      });
      spaceSrc.models2D.forEach((m2d) => {
        if (m2d.uuid) spaceModel2DUuids.add(m2d.uuid);
      });

      let floor: FloorModel | undefined = undefined;
      if (spaceSrc.floor) {
        const f = spaceSrc.floor;
        const tiles = f.tiles
          ? new TilesModel(f.tiles.length, f.tiles.width, f.tiles.rotation, f.tiles.jointWidth, f.tiles.jointColor, createAttributes(f.tiles.attributes))
          : undefined;

        floor = new FloorModel(tiles, f.texture, createAttributes(f.attributes));
        floor.setUUID(f.uuid);
      }

      // WALLS (space-local clones)
      const walls = spaceSrc.walls
        .map((w) => {
          const src = wallMap.get(w.uuid);
          if (!src) return null;
          return cloneWallFromSource(src);
        })
        .filter(Boolean) as any[];

      // PRODUCTS
      const products = spaceSrc.products.map((p) => {
        const m = new ProductModel(
          p.id.toString(),
          p.name,
          p.svgPath,
          1,
          p.dimensions.height ?? 0,
          p.dimensions.depth,
          new Vector3(p.position.x, p.position.y, p.position.z),
          p.rotation,
          p.fillColor,
          p.strokeColor,
          p.strokeWidth,
          p.texture,
          createAttributes(p.attributes),
          createObjects(p.objects),
          p.description,
          p.manufacturer,
          undefined,
          p.country,
        );
        m.setUUID(p.uuid);
        return m;
      });

      // ELEMENTS
      const elements = spaceSrc.elements.map((e) => {
        const m = new ElementModel(
          e.type,
          e.length,
          e.width,
          new Vector3(e.position.x, e.position.y, e.position.z),
          e.rotation,
          e.texture,
          e.description,
          e.useAsTextField,
          e.fontSize,
          createObject(e.object),
          createAttributes(e.attributes),
          e.sideC,
          e.triangleAngle
        );
        m.setUUID(e.uuid);
        return m;
      });

      // MODELS2D
      const models2D = spaceSrc.models2D.map((m2d) => {
        const m = new Model2DModel(
          m2d.id,
          m2d.name,
          m2d.filePath,
          m2d.fileFormat,
          m2d.fileSize,
          1,
          m2d.dimensions.depth,
          createObject(m2d.object),
          new Vector3(m2d.position.x, m2d.position.y, m2d.position.z),
          m2d.rotation,
          m2d.texture,
          createAttributes(m2d.attributes),
        );
        m.setUUID(m2d.uuid);
        return m;
      });

      // BOUNDARIES
      const boundaries = spaceSrc.boundaries.map((b) => {
        if (isPhysicalBoundary(b)) {
          const pb = new PhysicalBoundaryModel();
          pb.setUUID(b.uuid);
          pb.setWalls(b.walls.map((w) => wallMap.get(w.uuid)).filter(Boolean) as any[]);
          pb.setEdges(
            b.edges.map((e) => {
              const wall = wallMap.get(e.wall.uuid)!;
              const edge = new BoundaryEdge(wall, e.t0, e.t1, e.side, e.reversed);
              edge.setPoints(e.startPoint, e.endPoint);
              edge.setUUID(e.uuid);
              return edge;
            }),
          );
          return pb;
        }
        const vb = new VirtualBoundaryModel();
        vb.setUUID(b.uuid);
        return vb;
      });

      const space = new SpaceModel(floor, products, elements, models2D, boundaries, walls);
      space.setUUID(spaceSrc.uuid);

      room.addSpace(space);
    });

    level0.rooms.push(room);
  });

  // ------------------------------------------------------
  // FLOATING WALLS
  // ------------------------------------------------------
  level0.walls = (building.walls ?? []).filter((w) => w.uuid && !spaceWallUuids.has(w.uuid)).map((w) => cloneWallFromSource(w));

  // ------------------------------------------------------
  // FLOATING PRODUCTS
  // ------------------------------------------------------
  level0.products = (building.products ?? [])
    .filter((p) => p.uuid && !spaceProductUuids.has(p.uuid))
    .map((p) => {
      const m = new ProductModel(
        p.id.toString(),
        p.name,
        p.svgPath,
        1,
        p.dimensions.height ?? 0,
        p.dimensions.depth,
        new Vector3(p.position.x, p.position.y, p.position.z),
        p.rotation,
        p.fillColor,
        p.strokeColor,
        p.strokeWidth,
        p.texture,
        createAttributes(p.attributes),
        createObjects(p.objects),
        p.description,
        p.manufacturer,
        undefined,
        p.country,
      );
      m.setUUID(p.uuid);
      return m;
    });

  // ------------------------------------------------------
  // FLOATING ELEMENTS
  // ------------------------------------------------------
  level0.elements = (building.elements ?? [])
    .filter((e) => e.uuid && !spaceElementUuids.has(e.uuid))
    .map((e) => {
      const m = new ElementModel(
        e.type,
        e.length,
        e.width,
        new Vector3(e.position.x, e.position.y, e.position.z),
        e.rotation,
        e.texture,
        e.description,
        e.useAsTextField,
        e.fontSize,
        createObject(e.object),
        createAttributes(e.attributes),
        e.sideC,
        e.triangleAngle
      );
      m.setUUID(e.uuid);
      return m;
    });

  // ------------------------------------------------------
  // FLOATING MODELS2D
  // ------------------------------------------------------
  level0.models2D = (building.models2D ?? [])
    .filter((m2d) => m2d.uuid && !spaceModel2DUuids.has(m2d.uuid))
    .map((m2d) => {
      const m = new Model2DModel(
        m2d.id,
        m2d.name,
        m2d.filePath,
        m2d.fileFormat,
        m2d.fileSize,
        1,
        m2d.dimensions.depth,
        createObject(m2d.object),
        new Vector3(m2d.position.x, m2d.position.y, m2d.position.z),
        m2d.rotation,
        m2d.texture,
        createAttributes(m2d.attributes),
      );
      m.setUUID(m2d.uuid);
      return m;
    });

  return buildingModel;
};
